import type { OfferPhase } from "@/lib/offer-calc";
import type { OfferLine } from "@/hooks/use-offers";

export const PHASE_ORDER: { key: OfferPhase; label: string }[] = [
  { key: "pl", label: "Prosjektledelse" },
  { key: "preprod", label: "Preproduksjon" },
  { key: "production", label: "Produksjon" },
  { key: "postprod", label: "Postproduksjon" },
  { key: "stills", label: "Stills" },
  { key: "reise", label: "Reise" },
];

export interface PhaseGroup {
  key: OfferPhase;
  label: string;
  lines: OfferLine[];
}

export function isLineVisible(l: OfferLine): boolean {
  return (l.quantity ?? 0) !== 0 || (l.amount ?? 0) !== 0;
}

/**
 * Interne faktureringspresiseringer som ikke skal ut til kunde.
 *
 * «Reisetid (faste timer)» og «Reisetid (medgått)» skiller HVORDAN reise
 * faktureres. Sondringen er vår egen, og trengs i editoren for å velge
 * riktig mal; kunden skal bare se «Reisetid» (Erik 30.09).
 *
 * Bevisst en eksplisitt liste, ikke «strip siste parentes»: beskrivende
 * parenteser som «Grip (utstyr)» og «Musikk (boks/royaltyfri)» er en del
 * av linjenavnet og skal stå.
 */
const INTERNAL_QUALIFIERS = ["faste timer", "medgått"];

/** Linjenavnet slik det skal vises i en eksport til kunde. */
export function exportLineDescription(description: string): string {
  const trimmed = description.trim();
  for (const q of INTERNAL_QUALIFIERS) {
    const suffix = `(${q})`;
    if (trimmed.toLowerCase().endsWith(suffix.toLowerCase())) {
      return trimmed.slice(0, -suffix.length).trim();
    }
  }
  return trimmed;
}

export function fmtPlain(n: number): string {
  return new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 0 }).format(n);
}

export function fmtQty(n: number): string {
  return new Intl.NumberFormat("nb-NO", { maximumFractionDigits: 2 }).format(n);
}

export function fmtKr(n: number): string {
  return `kr ${fmtPlain(n)}`;
}

/**
 * Kort overtidstekst i eksport (VIB-2087):
 *   "Herav 4t OT +500 kr/t"
 */
export function fmtOvertimeBreakdown(
  split: { overtime_qty: number; overtime_extra_rate: number; overtime_unit?: "t" | "d" },
  _totalQty: number,
  unitLabel: string,
): string {
  const unit = split.overtime_unit === "d" || split.overtime_unit === "t"
    ? split.overtime_unit
    : unitLabel;
  return `Herav ${fmtQty(split.overtime_qty)}${unit} OT +${fmtPlain(split.overtime_extra_rate)} kr/${unit}`;
}

/**
 * VIB-702: greedy-splitt av phase-blokker i to kolonner. Vi prøver å
 * balansere antall linjer (inkl. phase-headers er ikke talt) ved å legge
 * neste phase i den kolonnen med færrest linjer hittil. Rekkefølgen
 * innenfor hver kolonne beholdes (PHASE_ORDER → preprod før production
 * osv.) så kunden ser samme flyt som i 1-kolonne-rendering.
 */
export function splitPhasesToColumns(
  phases: PhaseGroup[],
): [PhaseGroup[], PhaseGroup[]] {
  const col1: PhaseGroup[] = [];
  const col2: PhaseGroup[] = [];
  let c1Lines = 0;
  let c2Lines = 0;
  for (const phase of phases) {
    if (c1Lines <= c2Lines) {
      col1.push(phase);
      c1Lines += phase.lines.length;
    } else {
      col2.push(phase);
      c2Lines += phase.lines.length;
    }
  }
  return [col1, col2];
}
